import { z } from 'zod';

import type { TFunction } from 'i18next';

import { stripHtml } from '@/helpers';

export const CERTIFICATE_NAME_MAX_LENGTH = 120;

export const CERTIFICATE_ITEM_SERVICE = 'service' as const;
export const CERTIFICATE_ITEM_CARD = 'card' as const;
export const CERTIFICATE_ITEM_DEPOSIT = 'deposit' as const;

export const CERTIFICATE_ITEM_KINDS = [
  CERTIFICATE_ITEM_SERVICE,
  CERTIFICATE_ITEM_CARD,
  CERTIFICATE_ITEM_DEPOSIT,
] as const;

const isPositiveNumber = (value: string) =>
  value.trim() !== '' && !isNaN(Number(value)) && Number(value) > 0;

const isPositiveInteger = (value: string) =>
  value.trim() !== '' && Number.isInteger(Number(value)) && Number(value) > 0;

const createCertificateItemRowSchema = (t: TFunction) =>
  z
    .object({
      record_id: z.number().nullable(),
      kind: z.enum(CERTIFICATE_ITEM_KINDS),
      sellable_id: z.string().nullable(),
      sellable_count: z.string(),
      deposit_amount: z.string(),
    })
    .superRefine((row, ctx) => {
      if (row.kind === CERTIFICATE_ITEM_DEPOSIT) {
        if (!isPositiveNumber(row.deposit_amount)) {
          ctx.addIssue({
            code: 'custom',
            message: t('validation_required'),
            path: ['deposit_amount'],
          });
        }
        return;
      }

      if (!row.sellable_id) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation_required'),
          path: ['sellable_id'],
        });
      }

      if (!isPositiveInteger(row.sellable_count)) {
        ctx.addIssue({
          code: 'custom',
          message: t('validation_invalid'),
          path: ['sellable_count'],
        });
      }
    });

export const createCatalogCertificateFormSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, { message: t('validation_required') })
        .max(CERTIFICATE_NAME_MAX_LENGTH, {
          message: t('validation_max_length', {
            count: CERTIFICATE_NAME_MAX_LENGTH,
          }),
        }),
      description: z
        .string()
        .optional()
        .transform((value) =>
          value && stripHtml(value).trim().length > 0 ? value : '',
        ),
      location_ids: z
        .array(z.string())
        .min(1, { message: t('validation_required') }),
      photo: z.union([z.instanceof(File), z.string(), z.null()]),
      available_online: z.boolean(),
      certificate_items: z
        .array(createCertificateItemRowSchema(t))
        .min(1, { message: t('validation_required') }),
    })
    .superRefine((values, ctx) => {
      const depositCount = values.certificate_items.filter(
        (item) => item.kind === CERTIFICATE_ITEM_DEPOSIT,
      ).length;

      if (depositCount > 1) {
        ctx.addIssue({
          code: 'custom',
          message: t('Certificate single deposit'),
          path: ['certificate_items'],
        });
      }
    });

export type CatalogCertificateFormValues = z.infer<
  ReturnType<typeof createCatalogCertificateFormSchema>
>;

export type CatalogCertificateItemRow =
  CatalogCertificateFormValues['certificate_items'][number];
